import { useState, useEffect } from 'react';

export default function EmailSetup() {
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('Test Email from Your Platform');
  const [testMessage, setTestMessage] = useState('This is a test email to verify your email notification system is working correctly.');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    // Check if Supabase environment variables are configured
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
    
    setIsSupabaseConnected(!!(supabaseUrl && supabaseKey));
  }, []);

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setSendResult({ success: false, message: 'Please enter an email address' });
      return;
    }

    setIsSending(true);
    setSendResult(null);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          to: testEmail.trim(),
          subject: testSubject,
          message: testMessage,
          isHtml: false
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSendResult({ 
          success: true, 
          message: `Test email sent successfully to ${testEmail}` 
        });
      } else {
        setSendResult({ 
          success: false, 
          message: result.error || 'Failed to send email. Check your AWS SES configuration.' 
        });
      }
    } catch (error) {
      setSendResult({ 
        success: false, 
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}` 
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!isSupabaseConnected) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <i className="ri-error-warning-line text-amber-600 text-xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-amber-800 mb-3">
              Email Setup Required
            </h3>
            <div className="text-amber-700 space-y-3">
              <p className="font-medium">Follow these steps to enable email notifications:</p>
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li><strong>Connect Supabase:</strong> Use the Connect Supabase button above</li>
                <li><strong>Set up AWS SES:</strong> Create an AWS account and configure SES</li>
                <li><strong>Verify sender email:</strong> Add your domain/email in AWS SES Console</li>
                <li><strong>Create IAM user:</strong> Generate access keys with SES permissions</li>
                <li><strong>Add secrets:</strong> Configure Edge Function with AWS credentials</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Test Email Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <i className="ri-mail-send-line text-blue-600 text-xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Test Email System</h3>
            <p className="text-sm text-gray-600">Send a test email to verify your notification system</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipient Email Address *
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter a verified email address"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={isSending}
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: In sandbox mode, this email must be verified in your AWS SES console
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={testSubject}
              onChange={(e) => setTestSubject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={isSending}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              disabled={isSending}
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={handleSendTestEmail}
              disabled={isSending || !testEmail.trim()}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {isSending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-2-line"></i>
                  <span>Send Test Email</span>
                </>
              )}
            </button>

            {sendResult && (
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm ${
                sendResult.success 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                <i className={`${sendResult.success ? 'ri-check-circle-line' : 'ri-error-warning-line'}`}></i>
                <span>{sendResult.message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Status */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <i className="ri-check-circle-line text-green-600 text-xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Amazon SES Edge Function Active
            </h3>
            <p className="text-green-700 text-sm">
              Email function deployed. Configure production access below.
            </p>
          </div>
        </div>
      </div>

      {/* Production Access Setup */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <i className="ri-mail-send-line text-blue-600 text-xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">
              Request SES Production Access
            </h3>
            
            <div className="text-blue-700 space-y-4">
              <div className="bg-blue-100 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center">
                  <i className="ri-information-line mr-2"></i>
                  Current Sandbox Limitations
                </h4>
                <ul className="text-sm space-y-1">
                  <li>• Can only send TO verified email addresses</li>
                  <li>• Limited to 200 emails per day</li>
                  <li>• Maximum 1 email per second</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Steps to Enable Production Access:</h4>
                <ol className="list-decimal list-inside space-y-3 text-sm">
                  <li>
                    <strong>Open AWS SES Console:</strong>
                    <br />
                    <a 
                      href="https://console.aws.amazon.com/ses" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-external-link-line mr-1 text-xs"></i>
                      Open SES Console
                    </a>
                  </li>
                  
                  <li>
                    <strong>Navigate to Account Dashboard:</strong>
                    <br />
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded mt-1 inline-block">
                      Left menu → Account dashboard
                    </span>
                  </li>
                  
                  <li>
                    <strong>Request Production Access:</strong>
                    <br />
                    <span className="text-xs">Click "Request production access" button</span>
                  </li>
                  
                  <li>
                    <strong>Fill Application Form:</strong>
                    <div className="ml-4 mt-2 space-y-2">
                      <div>
                        <span className="font-medium">Use Case:</span> Select "Transactional"
                      </div>
                      <div>
                        <span className="font-medium">Website URL:</span> Your business website
                      </div>
                      <div>
                        <span className="font-medium">Description:</span>
                        <div className="bg-gray-100 p-2 rounded text-xs mt-1">
                          "I need to send transactional emails (notifications, confirmations, alerts) 
                          from my business application to customers and users. This includes account 
                          notifications, project updates, and system alerts."
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Expected Volume:</span> Enter realistic daily email count
                      </div>
                    </div>
                  </li>
                  
                  <li>
                    <strong>Submit Request:</strong>
                    <br />
                    <span className="text-xs">Review and submit application</span>
                  </li>
                </ol>
              </div>

              <div className="bg-green-100 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center text-green-800">
                  <i className="ri-time-line mr-2"></i>
                  After Approval (Usually 24-48 hours)
                </h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Send to ANY email address (no verification needed)</li>
                  <li>• Up to 200 emails/day initially (auto-increases with usage)</li>
                  <li>• Higher sending rates available</li>
                  <li>• No sandbox restrictions</li>
                </ul>
              </div>

              <div className="bg-amber-100 rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center text-amber-800">
                  <i className="ri-lightbulb-line mr-2"></i>
                  Pro Tips for Approval
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Use a professional business email domain</li>
                  <li>• Provide detailed, legitimate use case description</li>
                  <li>• Have a working website with contact information</li>
                  <li>• Be specific about email types you'll send</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Configuration */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Current Configuration
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-600">Edge Function:</span>
            <div className="text-gray-800">send-email</div>
          </div>
          <div>
            <span className="font-medium text-gray-600">Status:</span>
            <div className="text-green-600 flex items-center">
              <i className="ri-check-line mr-1"></i>
              Deployed
            </div>
          </div>
          <div>
            <span className="font-medium text-gray-600">Required Secrets:</span>
            <div className="text-gray-800">AWS credentials configured</div>
          </div>
          <div>
            <span className="font-medium text-gray-600">Access Level:</span>
            <div className="text-amber-600 flex items-center">
              <i className="ri-lock-line mr-1"></i>
              Sandbox Mode
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
